// Global keyboard shortcuts, installed once on <svelte:window>.
//
//   /            focus search (and close any open sheet first)
//   ?            toggle help overlay
//   r            open a random visible entry
//   g 1..4       jump to register N (Nigredo → Rubedo)
//   g c          jump to currents (Струны)
//   g t          jump to top
//   j / k        next / previous chapter
//   Escape       handled in Sheet.svelte (close) — also closes help here
//
// Modals (sheet/help) suppress the rest: shortcuts only fire on the base layer
// unless they're modal-aware (Esc, ?, arrows in sheet).
//
// Type letter+digit sequences by tracking a 600ms window.

const SEQ_WINDOW_MS = 700;

export function makeShortcuts(opts: {
  focusSearch: () => void;
  jumpTo: (id: string) => void;
  chapters: () => string[];
  help: () => void;
  random: () => void;
}) {
  let lastKey = '';
  let lastAt = 0;

  return function onKeydown(e: KeyboardEvent) {
    // Typing into form fields: only Esc passes through.
    const t = e.target as HTMLElement;
    const inField =
      t &&
      (t.tagName === 'INPUT' ||
        t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' ||
        t.isContentEditable);

    // Escape is universal.
    if (e.key === 'Escape') return;

    if (inField) return;

    // Modifier chords: ignore so we don't eat browser shortcuts.
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    const now = Date.now();
    const fresh = now - lastAt < SEQ_WINDOW_MS;
    const prev = fresh ? lastKey : '';
    lastKey = e.key.toLowerCase();
    lastAt = now;

    // Two-key "g X" jump sequences.
    if (prev === 'g') {
      if (e.key >= '1' && e.key <= '4') {
        opts.jumpTo(`reg-${e.key}`);
        e.preventDefault();
        return;
      }
      if (lastKey === 'c') {
        opts.jumpTo('currents');
        e.preventDefault();
        return;
      }
      if (lastKey === 't') {
        opts.jumpTo('top');
        e.preventDefault();
        return;
      }
    }

    switch (e.key) {
      case '/':
        e.preventDefault();
        opts.focusSearch();
        break;
      case '?':
        e.preventDefault();
        opts.help();
        break;
      case 'r':
        e.preventDefault();
        opts.random();
        break;
      case 'j': {
        e.preventDefault();
        const chs = opts.chapters();
        advanceChapter(chs, +1);
        break;
      }
      case 'k': {
        e.preventDefault();
        const chs = opts.chapters();
        advanceChapter(chs, -1);
        break;
      }
    }
  };
}

function advanceChapter(chapters: string[], dir: 1 | -1) {
  // Find nearest chapter whose top is below (dir=+1) or above (dir=-1) the
  // rail-adjusted viewport midpoint.
  const mid = window.scrollY + window.innerHeight * 0.4;
  let best: { id: string; d: number } | null = null;
  for (const id of chapters) {
    const el = document.getElementById(id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const d = (top - mid) * dir;
    if (d > 0 && (!best || d < best.d)) best = { id, d };
  }
  if (best) document.getElementById(best.id)?.scrollIntoView({ behavior: 'smooth' });
}
